import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { projectAPI, domainAPI } from '../services/api';
import {
  SparklesIcon,
  ArrowLeftIcon,
  RocketLaunchIcon,
  BriefcaseIcon,
  DocumentTextIcon,
  CodeBracketIcon,
} from '@heroicons/react/24/outline';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import toast from 'react-hot-toast';

export default function CreateProject() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Mode selection: 'select', 'ai_autopilot', 'pm_powerhouse', 'software_dev'
  const [mode, setMode] = useState('select');
  const [methodology, setMethodology] = useState('AGILE_USER_STORY');
  const [uploadedFile, setUploadedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  
  // Software Dev Template state
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [softwareFormData, setSoftwareFormData] = useState({
    projectName: '',
    teamSize: 3,
    timelineWeeks: 8,
    additionalRequirements: '' // String for textarea input
  });

  const [formData, setFormData] = useState({
    goal: '',
    stakeholders: '',
    title: '',
    description: '',
    priority: 'medium',
    deadline: '',
    budget: '',
    start_date: '',
    creation_mode: 'ai_autopilot',
    source_methodology: 'AGILE_USER_STORY',
    initial_business_case: '',
    business_case_filename: '',
    business_case_filepath: '',
  });

  const createProjectMutation = useMutation({
    mutationFn: projectAPI.createProject,
    onSuccess: (data) => {
      toast.success('Project created successfully!');
      queryClient.invalidateQueries(['projects']);
      queryClient.invalidateQueries(['dashboard']);
      navigate(`/projects/${data.project_id}`);
    },
    onError: (error) => {
      toast.error('Failed to create project. Please try again.');
      console.error('Create project error:', error);
    },
  });

  const handleModeSelect = (selectedMode) => {
    setMode(selectedMode);
    setFormData({
      ...formData,
      creation_mode: selectedMode,
      source_methodology: selectedMode === 'ai_autopilot' ? 'AGILE_USER_STORY' : methodology,
    });
  };

  const handleMethodologyChange = (newMethodology) => {
    setMethodology(newMethodology);
    setFormData({
      ...formData,
      source_methodology: newMethodology,
    });
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const token = localStorage.getItem('auth_token');
      if (!token) {
        throw new Error('Please log in to upload files');
      }

      const API_BASE_URL = process.env.REACT_APP_API_URL || '';
      const response = await fetch(`${API_BASE_URL}/api/upload/business-case`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Upload failed');
      }

      const result = await response.json();

      setUploadedFile({
        name: result.filename,
        size: result.file_size,
        type: result.file_type,
      });

      setFormData(prev => ({
        ...prev,
        initial_business_case: result.extracted_text,
        business_case_filename: result.filename,
        business_case_filepath: result.filepath,
      }));

      toast.success(`File "${result.filename}" uploaded successfully!`);
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.message || 'Failed to upload file');
    } finally {
      setIsUploading(false);
    }
  };

  const handleRemoveFile = () => {
    setUploadedFile(null);
    setFormData(prev => ({
      ...prev,
      initial_business_case: '',
      business_case_filename: '',
      business_case_filepath: '',
    }));
  };

  // Fetch templates when software_dev mode is selected
  useEffect(() => {
    if (mode === 'software_dev') {
      domainAPI.getTemplates()
        .then(data => {
          setTemplates(data.templates || []);
        })
        .catch(error => {
          console.error('Failed to load templates:', error);
          toast.error('Failed to load templates');
        });
    }
  }, [mode]);

  const handleSoftwareFormChange = (e) => {
    const { name, value } = e.target;
    setSoftwareFormData(prev => ({
      ...prev,
      [name]: name === 'teamSize' || name === 'timelineWeeks' ? parseInt(value) || 0 : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (mode === 'ai_autopilot' && !formData.goal.trim()) {
      toast.error('Please enter a project goal');
      return;
    }

    if (mode === 'pm_powerhouse' && !formData.initial_business_case.trim()) {
      toast.error('Please enter your business case or upload a document');
      return;
    }

    if (mode === 'software_dev') {
      if (!selectedTemplate) {
        toast.error('Please select a template');
        return;
      }
      if (!softwareFormData.projectName.trim()) {
        toast.error('Please enter a project name');
        return;
      }

      try {
        // Generate project plan from template
        // Handle additionalRequirements whether it's a string or array
        const requirements = Array.isArray(softwareFormData.additionalRequirements)
          ? softwareFormData.additionalRequirements
          : (typeof softwareFormData.additionalRequirements === 'string' 
              ? softwareFormData.additionalRequirements.split('\n').filter(r => r.trim())
              : []);
        
        const projectPlan = await domainAPI.generateProject(
          selectedTemplate,
          softwareFormData.projectName,
          softwareFormData.teamSize,
          softwareFormData.timelineWeeks,
          requirements
        );

        // Create project with generated plan
        createProjectMutation.mutate({
          title: softwareFormData.projectName,
          description: `Software development project from ${selectedTemplate} template`,
          priority: 'medium',
          creation_mode: 'software_dev',
          source_methodology: 'SOFTWARE_DEV_TEMPLATE',
          initial_business_case: JSON.stringify(projectPlan, null, 2),
        });
      } catch (error) {
        console.error('Failed to generate project:', error);
        toast.error('Failed to generate project plan');
      }
      return;
    }

    // Prepare data for AI Autopilot mode
    const submitData = {
      ...formData,
      // Convert budget to number if provided
      budget: formData.budget ? parseFloat(formData.budget) : undefined,
      // Ensure dates are in correct format or undefined
      start_date: formData.start_date || undefined,
      deadline: formData.deadline || undefined,
    };

    // Remove empty strings to let backend use defaults
    Object.keys(submitData).forEach(key => {
      if (submitData[key] === '' || submitData[key] === null) {
        delete submitData[key];
      }
    });

    console.log('Submitting project data:', submitData);
    createProjectMutation.mutate(submitData);
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-4">
        <button
          onClick={() => mode === 'select' ? navigate(-1) : setMode('select')}
          className="btn-secondary flex items-center space-x-2"
        >
          <ArrowLeftIcon className="w-4 h-4" />
          <span>{mode === 'select' ? 'Back' : 'Change Mode'}</span>
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Create New Project</h1>
          <p className="text-gray-600">
            {mode === 'select' && 'Choose how you want to create your project'}
            {mode === 'ai_autopilot' && '🤖 AI Autopilot Mode - Let AI create your plan'}
            {mode === 'pm_powerhouse' && '👔 PM Powerhouse Mode - Translate your methodology'}
            {mode === 'software_dev' && '💻 Software Dev Templates - Pre-built project plans'}
          </p>
        </div>
      </div>

      {/* Mode Selection Screen */}
      {mode === 'select' && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* AI Autopilot Mode */}
          <button
            onClick={() => handleModeSelect('ai_autopilot')}
            className="group relative bg-white rounded-xl shadow-lg border-2 border-gray-200 hover:border-blue-500 hover:shadow-xl transition-all p-8 text-left"
          >
            <div className="absolute top-4 right-4">
              <div className="bg-blue-100 text-blue-600 px-3 py-1 rounded-full text-xs font-semibold">
                STARTUP
              </div>
            </div>

            <div className="flex items-center gap-4 mb-4">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center">
                <RocketLaunchIcon className="w-8 h-8 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900">AI Autopilot</h3>
                <p className="text-xs text-gray-500">For any project</p>
              </div>
            </div>

            <p className="text-sm text-gray-600 mb-4">
              Describe your goal and AI creates everything automatically.
            </p>

            <div className="space-y-1 mb-4">
              <div className="flex items-center gap-2 text-xs text-gray-700">
                <SparklesIcon className="w-3 h-3 text-blue-600" />
                <span>Zero experience needed</span>
              </div>
              <div className="flex items-center gap-2 text-xs text-gray-700">
                <SparklesIcon className="w-3 h-3 text-blue-600" />
                <span>Perfect for quick starts</span>
              </div>
            </div>

            <div className="mt-4 text-blue-600 font-semibold group-hover:translate-x-1 transition-transform text-sm">
              Start with AI →
            </div>
          </button>

          {/* Software Dev Templates Mode */}
          <button
            onClick={() => handleModeSelect('software_dev')}
            className="group relative bg-white rounded-xl shadow-lg border-2 border-gray-200 hover:border-green-500 hover:shadow-xl transition-all p-8 text-left"
          >
            <div className="absolute top-4 right-4">
              <div className="bg-green-100 text-green-600 px-3 py-1 rounded-full text-xs font-semibold">
                DEVELOPER
              </div>
            </div>

            <div className="flex items-center gap-4 mb-4">
              <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-green-600 rounded-xl flex items-center justify-center">
                <CodeBracketIcon className="w-8 h-8 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900">Dev Templates</h3>
                <p className="text-xs text-gray-500">For software projects</p>
              </div>
            </div>

            <p className="text-sm text-gray-600 mb-4">
              Pre-built templates for web apps, mobile apps, APIs with sprints and best practices.
            </p>

            <div className="space-y-1 mb-4">
              <div className="flex items-center gap-2 text-xs text-gray-700">
                <CodeBracketIcon className="w-3 h-3 text-green-600" />
                <span>Sprint planning included</span>
              </div>
              <div className="flex items-center gap-2 text-xs text-gray-700">
                <CodeBracketIcon className="w-3 h-3 text-green-600" />
                <span>Code review checklists</span>
              </div>
            </div>

            <div className="mt-4 text-green-600 font-semibold group-hover:translate-x-1 transition-transform text-sm">
              Use Template →
            </div>
          </button>

          {/* PM Powerhouse Mode */}
          <button
            onClick={() => handleModeSelect('pm_powerhouse')}
            className="group relative bg-white rounded-xl shadow-lg border-2 border-gray-200 hover:border-purple-500 hover:shadow-xl transition-all p-8 text-left"
          >
            <div className="absolute top-4 right-4">
              <div className="bg-purple-100 text-purple-600 px-3 py-1 rounded-full text-xs font-semibold">
                ENTERPRISE
              </div>
            </div>

            <div className="flex items-center gap-4 mb-4">
              <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl flex items-center justify-center">
                <BriefcaseIcon className="w-8 h-8 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900">PM Powerhouse</h3>
                <p className="text-xs text-gray-500">For PM professionals</p>
              </div>
            </div>

            <p className="text-sm text-gray-600 mb-4">
              Translate PRINCE2, Lean, Scrum documents into executable plans.
            </p>

            <div className="space-y-1 mb-4">
              <div className="flex items-center gap-2 text-xs text-gray-700">
                <DocumentTextIcon className="w-3 h-3 text-purple-600" />
                <span>Preserves methodology</span>
              </div>
              <div className="flex items-center gap-2 text-xs text-gray-700">
                <DocumentTextIcon className="w-3 h-3 text-purple-600" />
                <span>Full audit trail</span>
              </div>
            </div>

            <div className="mt-4 text-purple-600 font-semibold group-hover:translate-x-1 transition-transform text-sm">
              Translate Document →
            </div>
          </button>
        </div>
      )}

      {/* AI Autopilot Form */}
      {mode === 'ai_autopilot' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <form onSubmit={handleSubmit} className="card">
              <div className="card-header">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <RocketLaunchIcon className="w-5 h-5 text-blue-600" />
                  AI Autopilot Mode
                </h3>
                <p className="text-sm text-gray-600">
                  Describe your goal and let AI create the complete project plan
                </p>
              </div>

              <div className="card-body space-y-6">
                {/* Project Goal */}
                <div className="form-group">
                  <label htmlFor="goal" className="form-label">
                    Project Goal *
                  </label>
                  <textarea
                    id="goal"
                    name="goal"
                    required
                    rows={6}
                    className="form-textarea"
                    placeholder="Example: Build a mobile app for tracking fitness goals with workout plans, nutrition tracking, and progress analytics..."
                    value={formData.goal}
                    onChange={handleChange}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    💡 Tip: Be specific about what you want to achieve. Include key features, target users, and success criteria.
                  </p>
                </div>

              {/* Project Title */}
              <div className="form-group">
                <label htmlFor="title" className="form-label">
                  Project Title (Optional)
                </label>
                <input
                  id="title"
                  name="title"
                  type="text"
                  className="form-input"
                  placeholder="Enter a custom project title"
                  value={formData.title}
                  onChange={handleChange}
                />
              </div>

              {/* Description */}
              <div className="form-group">
                <label htmlFor="description" className="form-label">
                  Additional Description
                </label>
                <textarea
                  id="description"
                  name="description"
                  rows={3}
                  className="form-textarea"
                  placeholder="Any additional context or requirements..."
                  value={formData.description}
                  onChange={handleChange}
                />
              </div>

              {/* Budget and Dates */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="form-group">
                  <label htmlFor="budget" className="form-label">
                    Budget ($)
                  </label>
                  <input
                    id="budget"
                    name="budget"
                    type="number"
                    min="0"
                    step="1000"
                    className="form-input"
                    placeholder="e.g., 50000"
                    value={formData.budget}
                    onChange={handleChange}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="start_date" className="form-label">
                    Start Date
                  </label>
                  <input
                    id="start_date"
                    name="start_date"
                    type="date"
                    className="form-input"
                    value={formData.start_date}
                    onChange={handleChange}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="deadline" className="form-label">
                    End Date
                  </label>
                  <input
                    id="deadline"
                    name="deadline"
                    type="date"
                    className="form-input"
                    value={formData.deadline}
                    onChange={handleChange}
                  />
                </div>
              </div>

              {/* Priority */}
              <div className="form-group">
                <label htmlFor="priority" className="form-label">
                  Priority
                </label>
                <select
                  id="priority"
                  name="priority"
                  className="form-select"
                  value={formData.priority}
                  onChange={handleChange}
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="critical">Critical</option>
                </select>
              </div>

              {/* Stakeholders */}
              <div className="form-group">
                <label htmlFor="stakeholders" className="form-label">
                  Stakeholders
                </label>
                <input
                  id="stakeholders"
                  name="stakeholders"
                  type="text"
                  className="form-input"
                  placeholder="Enter email addresses separated by commas"
                  value={formData.stakeholders}
                  onChange={handleChange}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Team members and stakeholders who should be notified about project updates
                </p>
              </div>
            </div>

              <div className="card-footer">
                <div className="flex items-center justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => setMode('select')}
                    className="btn-secondary"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={createProjectMutation.isLoading}
                    className="btn-primary flex items-center space-x-2"
                  >
                    {createProjectMutation.isLoading ? (
                      <>
                        <LoadingSpinner size="sm" />
                        <span>Creating Project...</span>
                      </>
                    ) : (
                      <>
                        <SparklesIcon className="w-4 h-4" />
                        <span>Create with AI</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            </form>
          </div>

          {/* Sidebar - AI Autopilot Tips */}
          <div className="space-y-6">
            <div className="card">
              <div className="card-header">
                <h3 className="text-sm font-semibold text-gray-900">💡 Tips for Best Results</h3>
              </div>
              <div className="card-body">
                <ul className="space-y-3 text-sm text-gray-600">
                  <li className="flex gap-2">
                    <span className="text-blue-600">•</span>
                    <span>Be specific about your goals and success criteria</span>
                  </li>
                  <li className="flex gap-2">
                    <span className="text-blue-600">•</span>
                    <span>Mention key features or deliverables</span>
                  </li>
                  <li className="flex gap-2">
                    <span className="text-blue-600">•</span>
                    <span>Include target users or stakeholders</span>
                  </li>
                  <li className="flex gap-2">
                    <span className="text-blue-600">•</span>
                    <span>Specify any constraints (budget, timeline, tech)</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* PM Powerhouse Form */}
      {mode === 'pm_powerhouse' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <form onSubmit={handleSubmit} className="card">
              <div className="card-header">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <BriefcaseIcon className="w-5 h-5 text-purple-600" />
                  PM Powerhouse Mode - The Herald's Desk
                </h3>
                <p className="text-sm text-gray-600">
                  Paste your formal project document and AI will translate it into an executable plan
                </p>
              </div>

              <div className="card-body space-y-6">
                {/* Methodology Selector */}
                <div className="form-group">
                  <label htmlFor="methodology" className="form-label">
                    Source Methodology *
                  </label>
                  <select
                    id="methodology"
                    name="source_methodology"
                    className="form-select"
                    value={formData.source_methodology}
                    onChange={(e) => {
                      handleMethodologyChange(e.target.value);
                      handleChange(e);
                    }}
                  >
                    <option value="AGILE_USER_STORY">Simple Goal / Agile User Story</option>
                    <option value="PRINCE2_BUSINESS_CASE">PRINCE2 Business Case</option>
                    <option value="LEAN_A3_REPORT">Lean / Six Sigma (A3 Report)</option>
                    <option value="SCRUM_EPIC">Scrum Epic & User Stories</option>
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    Select the methodology your document follows
                  </p>
                </div>

                {/* File Upload or Text Input */}
                <div className="form-group">
                  <label className="form-label">
                    Project Document *
                  </label>

                  {/* Upload Area */}
                  {!uploadedFile && (
                    <div className="mb-4">
                      <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-purple-400 transition-colors">
                        <DocumentTextIcon className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                        <p className="text-sm text-gray-600 mb-2">
                          Upload a document or paste text below
                        </p>
                        <p className="text-xs text-gray-500 mb-4">
                          Supported: PDF, DOCX, TXT, MD (Max 10MB)
                        </p>
                        <label className="btn-secondary cursor-pointer inline-flex items-center">
                          <input
                            type="file"
                            className="hidden"
                            accept=".pdf,.docx,.doc,.txt,.md"
                            onChange={handleFileUpload}
                            disabled={isUploading}
                          />
                          {isUploading ? (
                            <>
                              <LoadingSpinner size="sm" />
                              <span className="ml-2">Uploading...</span>
                            </>
                          ) : (
                            <>
                              <DocumentTextIcon className="w-4 h-4 mr-2" />
                              Choose File
                            </>
                          )}
                        </label>
                      </div>
                    </div>
                  )}

                  {/* Uploaded File Display */}
                  {uploadedFile && (
                    <div className="mb-4 p-4 bg-purple-50 border border-purple-200 rounded-lg">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                            <DocumentTextIcon className="w-5 h-5 text-purple-600" />
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-900">{uploadedFile.name}</p>
                            <p className="text-xs text-gray-500">
                              {(uploadedFile.size / 1024).toFixed(1)} KB • {uploadedFile.type}
                            </p>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={handleRemoveFile}
                          className="text-red-600 hover:text-red-700 text-sm font-medium"
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Text Area */}
                  <textarea
                    id="initial_business_case"
                    name="initial_business_case"
                    required
                    rows={uploadedFile ? 8 : 16}
                    className="form-textarea font-mono text-sm"
                    placeholder={
                      uploadedFile
                        ? "Extracted text from uploaded document (you can edit this)..."
                        : formData.source_methodology === 'PRINCE2_BUSINESS_CASE'
                        ? "Paste your PRINCE2 Business Case here...\n\nExample:\nBusiness Justification:\n- Problem/Opportunity: ...\n- Expected Benefits: ...\n\nProject Scope:\n- In Scope: ...\n- Out of Scope: ...\n\nRisks:\n- Risk 1: ..."
                        : formData.source_methodology === 'LEAN_A3_REPORT'
                        ? "Paste your Lean A3 Report here...\n\nExample:\nProblem Statement: ...\nCurrent State: ...\nTarget Condition: ...\nRoot Cause Analysis: ...\nCountermeasures: ..."
                        : formData.source_methodology === 'SCRUM_EPIC'
                        ? "Paste your Scrum Epic and User Stories here...\n\nExample:\nEpic: As a [user type], I want to [goal]...\n\nUser Stories:\n- As a [user], I want to [action], so that I can [benefit]\n- ..."
                        : "Paste your project goal or document here..."
                    }
                    value={formData.initial_business_case}
                    onChange={handleChange}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    📜 This document will be preserved for audit trail and provenance tracking
                  </p>
                </div>

                {/* Optional Fields */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="form-group">
                    <label htmlFor="priority" className="form-label">
                      Priority
                    </label>
                    <select
                      id="priority"
                      name="priority"
                      className="form-select"
                      value={formData.priority}
                      onChange={handleChange}
                    >
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                      <option value="critical">Critical</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label htmlFor="stakeholders" className="form-label">
                      Stakeholders
                    </label>
                    <input
                      id="stakeholders"
                      name="stakeholders"
                      type="text"
                      className="form-input"
                      placeholder="Emails separated by commas"
                      value={formData.stakeholders}
                      onChange={handleChange}
                    />
                  </div>
                </div>
              </div>

              <div className="card-footer">
                <div className="flex items-center justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => setMode('select')}
                    className="btn-secondary"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={createProjectMutation.isLoading}
                    className="btn-primary flex items-center space-x-2"
                  >
                    {createProjectMutation.isLoading ? (
                      <>
                        <LoadingSpinner size="sm" />
                        <span>Translating Document...</span>
                      </>
                    ) : (
                      <>
                        <DocumentTextIcon className="w-4 h-4" />
                        <span>Translate & Create</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            </form>
          </div>

          {/* Sidebar - PM Powerhouse Info */}
          <div className="space-y-6">
            <div className="card">
              <div className="card-header">
                <h3 className="text-sm font-semibold text-gray-900">📚 Supported Methodologies</h3>
              </div>
              <div className="card-body">
                <div className="space-y-4 text-sm">
                  <div>
                    <h4 className="font-semibold text-purple-900">PRINCE2</h4>
                    <p className="text-gray-600 text-xs">Business Case, Project Brief, PID</p>
                  </div>
                  <div>
                    <h4 className="font-semibold text-purple-900">Lean / Six Sigma</h4>
                    <p className="text-gray-600 text-xs">A3 Reports, DMAIC documentation</p>
                  </div>
                  <div>
                    <h4 className="font-semibold text-purple-900">Scrum / Agile</h4>
                    <p className="text-gray-600 text-xs">Epics, User Stories, Product Backlog</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="card bg-purple-50 border-purple-200">
              <div className="card-body">
                <h4 className="text-sm font-semibold text-purple-900 mb-2">🔒 Provenance Tracking</h4>
                <p className="text-xs text-purple-700">
                  Your original document is preserved for full audit trail and compliance.
                  AI translates it while respecting your methodology.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Software Dev Templates Form */}
      {mode === 'software_dev' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <form onSubmit={handleSubmit} className="card">
              <div className="card-header">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <CodeBracketIcon className="w-5 h-5 text-green-600" />
                  Software Dev Templates
                </h3>
                <p className="text-sm text-gray-600">
                  Choose a pre-built template and customize for your project
                </p>
              </div>

              <div className="card-body space-y-6">
                {/* Template Selection */}
                <div className="form-group">
                  <label htmlFor="template" className="form-label">
                    Project Template *
                  </label>
                  <select
                    id="template"
                    required
                    className="form-select"
                    value={selectedTemplate}
                    onChange={(e) => setSelectedTemplate(e.target.value)}
                  >
                    <option value="">Select a template...</option>
                    {templates.map(template => (
                      <option key={template} value={template}>
                        {template.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    💡 Each template includes phases, tasks, timeline estimates, and best practices
                  </p>
                </div>

                {/* Project Name */}
                <div className="form-group">
                  <label htmlFor="projectName" className="form-label">
                    Project Name *
                  </label>
                  <input
                    id="projectName"
                    name="projectName"
                    type="text"
                    required
                    className="form-input"
                    placeholder="My Awesome Software Project"
                    value={softwareFormData.projectName}
                    onChange={handleSoftwareFormChange}
                  />
                </div>

                {/* Team Size and Timeline */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="form-group">
                    <label htmlFor="teamSize" className="form-label">
                      Team Size
                    </label>
                    <input
                      id="teamSize"
                      name="teamSize"
                      type="number"
                      min="1"
                      max="50"
                      className="form-input"
                      placeholder="3"
                      value={softwareFormData.teamSize}
                      onChange={handleSoftwareFormChange}
                    />
                    <p className="text-xs text-gray-500 mt-1">Number of developers</p>
                  </div>

                  <div className="form-group">
                    <label htmlFor="timelineWeeks" className="form-label">
                      Timeline (weeks)
                    </label>
                    <input
                      id="timelineWeeks"
                      name="timelineWeeks"
                      type="number"
                      min="1"
                      max="104"
                      className="form-input"
                      placeholder="8"
                      value={softwareFormData.timelineWeeks}
                      onChange={handleSoftwareFormChange}
                    />
                    <p className="text-xs text-gray-500 mt-1">Project duration</p>
                  </div>
                </div>

                {/* Additional Requirements */}
                <div className="form-group">
                  <label htmlFor="additionalRequirements" className="form-label">
                    Additional Requirements (Optional)
                  </label>
                  <textarea
                    id="additionalRequirements"
                    name="additionalRequirements"
                    rows={4}
                    className="form-textarea"
                    placeholder="Enter any specific requirements, one per line..."
                    value={softwareFormData.additionalRequirements}
                    onChange={handleSoftwareFormChange}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    📝 Add specific features, integrations, or constraints
                  </p>
                </div>

                {/* Submit Button */}
                <div className="flex items-center justify-end space-x-3 pt-4 border-t">
                  <button
                    type="button"
                    onClick={() => setMode('select')}
                    className="btn-secondary"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={createProjectMutation.isLoading}
                    className="btn-primary flex items-center space-x-2"
                  >
                    {createProjectMutation.isLoading ? (
                      <>
                        <LoadingSpinner size="sm" />
                        <span>Generating Project...</span>
                      </>
                    ) : (
                      <>
                        <CodeBracketIcon className="w-4 h-4" />
                        <span>Generate Project</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            </form>
          </div>

          {/* Sidebar - Software Dev Info */}
          <div className="space-y-6">
            <div className="card">
              <div className="card-header">
                <h3 className="text-sm font-semibold text-gray-900">📦 Available Templates</h3>
              </div>
              <div className="card-body">
                <div className="space-y-4 text-sm">
                  <div>
                    <h4 className="font-semibold text-green-900">Web App</h4>
                    <p className="text-gray-600 text-xs">Full-stack web application with frontend, backend, and database</p>
                  </div>
                  <div>
                    <h4 className="font-semibold text-green-900">Mobile App</h4>
                    <p className="text-gray-600 text-xs">iOS/Android app with native or cross-platform development</p>
                  </div>
                  <div>
                    <h4 className="font-semibold text-green-900">API Service</h4>
                    <p className="text-gray-600 text-xs">RESTful API with authentication, database, and documentation</p>
                  </div>
                  <div>
                    <h4 className="font-semibold text-green-900">Microservices</h4>
                    <p className="text-gray-600 text-xs">Distributed system with multiple services and orchestration</p>
                  </div>
                  <div>
                    <h4 className="font-semibold text-green-900">Data Pipeline</h4>
                    <p className="text-gray-600 text-xs">ETL pipeline with data ingestion, processing, and storage</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="card bg-green-50 border-green-200">
              <div className="card-body">
                <h4 className="text-sm font-semibold text-green-900 mb-2">✨ What's Included</h4>
                <ul className="text-xs text-green-700 space-y-1">
                  <li>• Sprint planning with velocity</li>
                  <li>• Code review checklists</li>
                  <li>• Best practices & workflows</li>
                  <li>• Task breakdowns by phase</li>
                  <li>• Timeline estimates</li>
                  <li>• Team role assignments</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
